import { defineStore } from 'pinia';
import useTenantSubscriptionStore from '@/apps/billing-shared/src/store/tenant-subscription';
import employeesGet from '@/apps/hr/src/lib/request/webapp/employees-get';
import {
  ADD_ON_LEARN_MORE_URLS,
  addOns,
  targetEventSourceMap,
  EVENTS,
} from '@/apps/billing-shared/src/definitions';
import {
  NEWSFEED_EVENTS,
  NEWSFEED_POST_TYPE,
} from '@webapp-next/modules/newsfeed/analytics';
import getInstallationCountryCode from '@webapp-next/lib/facades/get-installation-country.js';
import { getTranslations } from '@webapp-next/lib/i18next';
import { isEnterprise } from '@webapp-next/lib/company-subscription';
import { getRouter } from '@webapp-next/router/helpers';
import { shouldEnableHrPaywallLogic } from '@apps-hr/lib/request/utils/hr-feature-flags';
import { getDataAnalyticsService } from '@webapp-next/lib/tracking/get-data-analytics';

import getVueEventBus from '@webapp-next/lib/vue-event-bus';
import VueEvents from '@webapp-next/global-libs/vue-events';

const VueEventBus = getVueEventBus();

const useHrUpgradeStore = defineStore('apps-hr:upgrade', {
  state: () => {
    return {
      isUpgradeModalVisible: false,
      upgradeModalScreen: '',
      isSuccessVisible: false,
      isSubscribed: false,
      targetFeature: '',
      targetOnboardEmployeeId: '',
      activeUsers: null,
      loadError: null,
      isLoading: false,
    };
  },
  getters: {
    isHrBlocked: () => {
      return () => {
        const tenantSubscriptionStoreFactory = useTenantSubscriptionStore;
        let tenantSubscriptionStore;

        try {
          tenantSubscriptionStore =
            typeof tenantSubscriptionStoreFactory === 'function'
              ? tenantSubscriptionStoreFactory()
              : tenantSubscriptionStoreFactory;
        } catch (error) {
          return false;
        }

        if (!tenantSubscriptionStore) {
          return false;
        }

        const hrAddOnId = addOns.HR_ADD_ON_ID;
        const isAddOnBlocked = tenantSubscriptionStore.isAddOnBlocked;
        const isEoCustomerBlocked = tenantSubscriptionStore.isEoCustomerBlocked;

        const addOnBlocked =
          typeof isAddOnBlocked === 'function'
            ? isAddOnBlocked(hrAddOnId)
            : Boolean(isAddOnBlocked);

        if (addOnBlocked) {
          return addOnBlocked;
        }

        return typeof isEoCustomerBlocked === 'function'
          ? isEoCustomerBlocked(hrAddOnId)
          : Boolean(isEoCustomerBlocked);
      };
    },
    isBeforeHrAddOnRelease: () => {
      return dateString => {
        const inputDate = new Date(Date.parse(dateString));
        const september1 = new Date('2023-10-01');
        return inputDate && inputDate < september1;
      };
    },
    isOnPayPerUseOnboarding: () => {
      return () => {
        const tenantSubscriptionStore = useTenantSubscriptionStore();

        const isEoCustomer = tenantSubscriptionStore.isEoCustomerPendingMigration(
          addOns.HR_ADD_ON_ID
        );

        if (isEoCustomer) {
          return true;
        }

        if (tenantSubscriptionStore.canAccessAddOn(addOns.HR_ADD_ON_ID)) {
          return false;
        }

        return !shouldEnableHrPaywallLogic();
      };
    },
    getLearnMoreUrl: () => {
      return () => {
        const tenantSubscriptionStore = useTenantSubscriptionStore();

        const countryCode = getInstallationCountryCode();
        const isEoCustomer = tenantSubscriptionStore.isEoCustomerPendingMigration(
          addOns.HR_ADD_ON_ID
        );
        const addOnLearnMorUrls =
          ADD_ON_LEARN_MORE_URLS[addOns.HR_ADD_ON_ID].tenant[countryCode];

        return isEoCustomer || tenantSubscriptionStore.hasCustomPricing()
          ? addOnLearnMorUrls.helpArticle
          : addOnLearnMorUrls.websitePage;
      };
    },
    getNewsFeedPostCopy: () => {
      return () => {
        const t = getTranslations({
          app: 'hr',
          module: 'upgrade',
          file: 'post-message',
        });
        const tenantSubscriptionStore = useTenantSubscriptionStore();
        const price = tenantSubscriptionStore.getSubscriptionDisplayPrice(
          addOns.HR_ADD_ON_ID
        );
        const period = tenantSubscriptionStore.getAddOnPricing(
          addOns.HR_ADD_ON_ID
        )?.pricing?.period;

        const getMessageCopy = () => {
          if (!isEnterprise()) {
            return tenantSubscriptionStore.isAddOnTrialAvailable(
              addOns.HR_ADD_ON_ID
            )
              ? t('intro') +
                  `\n\n${t('trial-eligible', {
                    price,
                    period,
                  })}`
              : t('intro') +
                  `\n\n${t('not-trial-eligible', {
                    price,
                    period,
                  })}`;
          }
          return t('intro');
        };

        const messageCopy =
          getMessageCopy() +
          `\n\n${t('more-info', {
            url: useHrUpgradeStore().getLearnMoreUrl(),
          })}`;

        return messageCopy;
      };
    },
  },
  actions: {
    setIsUpgradeModalVisible(show = false, eventDetails) {
      const tenantSubscriptionStore = useTenantSubscriptionStore();

      if (show === this.isUpgradeModalVisible) {
        return;
      }

      this.isUpgradeModalVisible = show;

      let eventDetailsName = 'Introducing Deputy HR modal';

      if (tenantSubscriptionStore.isAddOnTrialAvailable(addOns.HR_ADD_ON_ID)) {
        eventDetailsName = 'Deputy HR trial modal';
      } else if (this.isHrBlocked()) {
        eventDetailsName = 'Deputy HR upsell modal';
      }

      if (eventDetails) {
        this.upgradeModalScreen = eventDetails.screen;

        const target = targetEventSourceMap[eventDetails.source];
        this.targetFeature = target || null;

        const analyticsService = getDataAnalyticsService();

        if (eventDetails?.addOnDetailsName) {
          const isTrial =
            eventDetails?.isTrial ??
            tenantSubscriptionStore.isAddOnTrialAvailable(addOns.HR_ADD_ON_ID);
          analyticsService.track(
            {
              name: EVENTS.ADD_ON_SUBSCRIBE_STARTED,
              screen: this.upgradeModalScreen,
            },
            {
              source: eventDetails.source,
              add_on: {
                details: {
                  name: eventDetails?.addOnDetailsName,
                  isTrial,
                },
              },
            }
          );

          return;
        }

        analyticsService.track(
          {
            name: 'VIEW_OPENED',
            screen: this.upgradeModalScreen,
          },
          {
            source: eventDetails.source,
            view: {
              details: {
                name: eventDetailsName,
                type: 'POP_UP',
              },
            },
          }
        );
      }
    },
    setIsSuccessVisible(show = false) {
      this.isSuccessVisible = show;
    },
    setTargetFeature(target) {
      this.targetFeature = target;
    },
    setTargetOnboardEmployeeId(employeeId) {
      this.targetOnboardEmployeeId = employeeId;
    },
    async getTotalEmployees() {
      try {
        this.isLoading = true;
        const { headers } = await employeesGet();

        this.activeUsers = headers['x-deputy-total-count'];
      } catch (e) {
        this.loadError = e;
      } finally {
        this.isLoading = false;
      }
    },
    launchNewMessageModal(eventDetails) {
      const router = getRouter();

      const messageCopy = useHrUpgradeStore().getNewsFeedPostCopy();

      this.trackLaunchNewMessageModal(eventDetails);

      VueEventBus.$emit(VueEvents.NEWSFEED.NEWSFEED_SHOW_CREATE_POST, {
        messageContent: messageCopy,
      });

      router.push('#newsfeed');
    },
    trackLaunchNewMessageModal(eventDetails) {
      const analyticsService = getDataAnalyticsService();
      analyticsService.track(
        {
          name: NEWSFEED_EVENTS.NEWSFEED_POST_ADD_STARTED,
          screen: eventDetails.screen,
        },
        {
          source: eventDetails.source,
          newsfeedPost: {
            details: {
              postType: NEWSFEED_POST_TYPE.POST,
            },
          },
        }
      );
    },
  },
});

export default useHrUpgradeStore;
